
import { Injectable, OnDestroy, computed, effect, inject, signal, DOCUMENT } from '@angular/core';

export type Theme = 'light' | 'dark' | 'auto' | string;

interface ITheme {
  value: 'light' | 'dark' | 'auto' | string;
  viewValue: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeManagerService implements OnDestroy {
  readonly defaultTheme: string = 'auto';

  theme = signal<Theme>(this.defaultTheme);
  currentAppliedTheme = signal<Theme>(this.defaultTheme);
  public isDarkTheme = computed(() => this.currentAppliedTheme() === 'dark');

  private _document = inject(DOCUMENT);
  private prefersDarkTheme = window.matchMedia('(prefers-color-scheme: dark)');
  private themeClassMap: { [key: string]: string } = {
    light: 'light',
    dark: 'dark',
  };

  private isSyncfusionImported = true;
  private syncFusionDarkThemeClass = 'e-dark-mode';
  private readonly handleSystemThemeChange = (event: MediaQueryListEvent) => {
    if (this.theme() === 'auto') {
      this.applySystemTheme();
    }
  };

  constructor() {
    //this.applyCurrentTheme();

    this.prefersDarkTheme.addEventListener('change', this.handleSystemThemeChange);

    effect(() => {
      this.applyCurrentTheme();
    });
  }

  private applyCurrentTheme() {
    const selectedTheme = this.theme();
    if (selectedTheme === 'auto') {
      this.applySystemTheme();
    } else {
      this.applyTheme(selectedTheme);
    }
  }

  private applySystemTheme() {
    const systemTheme = this.prefersDarkTheme.matches ? 'dark' : 'light';
    this.applyTheme(systemTheme);
  }

  private applyTheme(theme: Theme) {
    Object.values(this.themeClassMap).forEach(cssClass => {
      this._document.documentElement.classList.remove(cssClass);
    });

    const themeClass = this.themeClassMap[theme] || theme;
    this._document.documentElement.classList.add(themeClass);
    this.currentAppliedTheme.set(theme);

    this.applySyncfusionTheme(theme);
  }

  toggleTheme(theme?: Theme) {
    const validThemes = ['auto', ...Object.keys(this.themeClassMap)];

    if (theme) {
      if (!this.isValidTheme(theme, validThemes)) {
        console.error(`Tema non valido: ${theme}. Temi validi: ${validThemes.join(', ')}`);
        return;
      }

      this.theme.set(theme);
    } else {
      const currentIndex = validThemes.indexOf(this.theme());
      const nextTheme = validThemes[(currentIndex + 1) % validThemes.length];
      this.theme.set(nextTheme);
    }
  }

  private isValidTheme(theme: Theme, validThemes: string[]): boolean {
    return validThemes.includes(theme);
  }

  private applySyncfusionTheme(theme: Theme) {
    if (!this.isSyncfusionImported) return;

    this._document.documentElement.classList.remove(this.syncFusionDarkThemeClass);

    if (theme === 'dark') {
      this._document.documentElement.classList.add(this.syncFusionDarkThemeClass);
    }
  }

  enableSyncfusionIntegration() {
    this.isSyncfusionImported = true;
  }

  ngOnDestroy() {
    this.prefersDarkTheme.removeEventListener('change', this.handleSystemThemeChange);
  }

  getAvailableThemes(): ITheme[] {
    const validThemes = Object.keys(this.themeClassMap).map(themeKey => ({
      value: themeKey,
      viewValue: themeKey.toUpperCase(),
    }));

    validThemes.unshift({
      value: 'auto',
      viewValue: 'AUTO',
    });

    return validThemes;
  }
}
